import { GridColDef } from '@mui/x-data-grid';
import { useCallback, useMemo } from 'react';
import { addItemsBaseColumnProps } from './useAddItemsBaseColumns';
import { dataGridBoldClass } from '@shared/styles';
import { useCampaignTFunction } from '@retail/campaign/i18n';
import { formatPrice } from '@retail/products/utils';
import { useTranslation } from 'react-i18next';
import { BaseMgSupplierCampaignItem } from '@retail/campaign/types';
import {
  calculatePriceWithoutVat,
  calculateSalesCoverageFromCostAndSalesPrice,
} from '@retail/utils';
import { useFormContext } from 'react-hook-form';
import { AddCampaignItemsSchema, BaseCampaignItemSchema } from './addItemsSchema';

export const useSalesCoverageColumns = () => {
  const t = useCampaignTFunction();
  const { i18n } = useTranslation();
  const { watch } = useFormContext<AddCampaignItemsSchema<BaseCampaignItemSchema>>();

  const getSalesPriceCoverage = useCallback(
    ({ mgSupplierItemId }: BaseMgSupplierCampaignItem) => {
      const formItem = watch('items')[mgSupplierItemId];
      const purchasePrice = formItem?.campaignPurchasePrice;
      const salesPrice = formItem?.campaignSalesPrice;

      return salesPrice && purchasePrice
        ? calculateSalesCoverageFromCostAndSalesPrice(
            purchasePrice,
            calculatePriceWithoutVat(salesPrice, true)
          )
        : undefined;
    },
    [watch]
  );

  return useMemo(() => {
    const getSalesCoverageColumn = (): GridColDef<BaseMgSupplierCampaignItem> => ({
      ...addItemsBaseColumnProps,
      cellClassName: () => dataGridBoldClass,
      width: 120,
      field: 'salesCoverage',
      headerAlign: 'center',
      align: 'center',
      headerName: t('campaign.columns.campaignSalesCoverage'),
      sortable: true,
      renderCell: ({ row }) => {
        const salesPriceCoverage = getSalesPriceCoverage(row);

        return Number.isFinite(salesPriceCoverage)
          ? `${formatPrice(Number((salesPriceCoverage as number).toFixed(2)), i18n.language)}%`
          : '-';
      },
      valueGetter: (_, row) => getSalesPriceCoverage(row),
    });

    return { getSalesCoverageColumn };
  }, [getSalesPriceCoverage, i18n.language, t]);
};
